<template>
  <div class="dropdown-checkbox-wrapper">
    <!-- Container with chips and dropdown trigger -->
    <div class="filter-select-container" @click.stop>
      <div class="filter-select-inner" @click="toggleOverlay">
        <!-- Selected items as chips -->
        <span
          v-for="item in selectedItems"
          :key="item.id"
          class="select2-selection__choice"
        >
          <span
            class="select2-selection__choice__remove"
            @click.stop="removeItem(item.id)"
            >×</span
          >
          {{ item.name }}
        </span>

        <!-- Placeholder when nothing selected -->
        <span
          v-if="selectedItems.length === 0"
          class="filter-select-placeholder"
        >
          {{ placeholder }}
        </span>

        <!-- Arrow indicator -->
        <i
          class="fa filter-select-arrow"
          :class="isOpen ? 'fa-caret-up' : 'fa-caret-down'"
        ></i>
      </div>

      <!-- Dropdown Menu -->
      <div v-if="isOpen" class="filter-dropdown-content" @click.stop>
        <div class="filter-dropdown-search">
          <input
            type="text"
            class="form-control"
            placeholder="Search..."
            v-model="searchQuery"
            ref="searchInput"
          />
        </div>

        <div class="filter-dropdown-body">
          <div class="filter-dropdown-items">
            <div
              v-for="item in filteredItems"
              :key="item.id"
              class="filter-dropdown-item"
              @click="toggleItem(item.id)"
            >
              <div class="custom-control custom-checkbox" @click.stop>
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="
                    'checkbox-' +
                    String(filterId).replace(/\./g, '-') +
                    '-' +
                    item.id
                  "
                  :value="parseInt(item.id)"
                  v-model="internalValue"
                  @change="onCheckboxChange"
                />
                <label
                  class="custom-control-label"
                  :for="
                    'checkbox-' +
                    String(filterId).replace(/\./g, '-') +
                    '-' +
                    item.id
                  "
                >
                  {{ item.name }}
                </label>
              </div>
            </div>
          </div>
          <div
            v-if="filteredItems.length === 0"
            class="text-muted text-center py-3"
          >
            No items found
          </div>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div
      v-if="isOpen"
      class="filter-dropdown-backdrop"
      @click="closeOverlay"
    ></div>
  </div>
</template>

<script>
import Vue from "vue";

export default Vue.component("dropdown-checkbox", {
  data() {
    return {
      isOpen: false,
      searchQuery: "",
      internalValue: [],
    };
  },

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: "Select items",
    },
    filterId: {
      type: String,
      required: true,
    },
  },

  computed: {
    filteredItems() {
      if (!this.searchQuery) {
        return this.items;
      }
      const query = this.searchQuery.toLowerCase();
      return this.items.filter((item) =>
        item.name.toLowerCase().includes(query),
      );
    },
    selectedItems() {
      return this.items.filter((item) =>
        this.internalValue.includes(parseInt(item.id)),
      );
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.internalValue = newValue.map((v) => parseInt(v));
      },
    },
  },

  mounted() {
    this.$root.$on("dropdown-opened", this.onOtherDropdownOpened);
    this.$root.$on("close-all-dropdowns", this.closeOverlay);
    document.addEventListener("click", this.handleDocumentClick);
  },

  beforeDestroy() {
    this.$root.$off("dropdown-opened", this.onOtherDropdownOpened);
    this.$root.$off("close-all-dropdowns", this.closeOverlay);
    document.removeEventListener("click", this.handleDocumentClick);
  },

  methods: {
    toggleOverlay() {
      if (this.isOpen) {
        this.closeOverlay();
      } else {
        this.openOverlay();
      }
    },

    openOverlay() {
      this.isOpen = true;
      this.searchQuery = "";
      this.$root.$emit("dropdown-opened", this._uid);
      this.$emit("dropdown-opened");
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },

    closeOverlay() {
      this.isOpen = false;
      this.searchQuery = "";
      this.$emit("dropdown-closed");
    },

    onCheckboxChange() {
      this.$emit("input", this.internalValue);
    },

    removeItem(itemId) {
      this.internalValue = this.internalValue.filter(
        (id) => id !== parseInt(itemId),
      );
      this.$emit("input", this.internalValue);
    },

    toggleItem(itemId) {
      const id = parseInt(itemId);
      const index = this.internalValue.indexOf(id);
      if (index > -1) {
        this.internalValue.splice(index, 1);
      } else {
        this.internalValue.push(id);
      }
      this.$emit("input", this.internalValue);
    },

    handleDocumentClick(event) {
      if (this.isOpen && this.$el && !this.$el.contains(event.target)) {
        this.closeOverlay();
      }
    },

    onOtherDropdownOpened(uid) {
      if (uid !== this._uid) {
        this.closeOverlay();
      }
    },
  },
});
</script>

<style scoped lang="scss">
.dropdown-checkbox-wrapper {
  position: relative;
  width: 100%;
}

/* Main container styling - looks like Select2 */
.filter-select-container {
  position: relative;
  width: 100%;
}

.filter-select-inner {
  background-color: #2a3439;
  border: 1px solid #4a5459;
  border-radius: 4px;
  padding: 0.375rem 2rem 0.375rem 0.5rem;
  min-height: 38px;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  position: relative;

  &:hover {
    border-color: #5a6469;
  }
}

/* Arrow indicator */
.filter-select-arrow {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: #c9c6c3;
  pointer-events: none;
}

/* Placeholder text */
.filter-select-placeholder {
  color: #6c757d;
  font-size: 14px;
}

/* Selected item chips - using existing Select2 styles */
.select2-selection__choice {
  float: none;
  margin: 0;
  font-size: 13px;
  padding: 3px 8px 3px 5px;
  display: inline-flex;
  align-items: center;
  color: #ccd4d8;
  border: 1px #53585d solid;
  background-color: #1d2228;
  border-radius: 3px;
}

.select2-selection__choice__remove {
  color: #ccd4d8;
  font-weight: bold;
  margin-right: 5px;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;

  &:hover {
    color: #fff;
  }
}

/* Backdrop for clicking outside */
.filter-dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
}

/* Dropdown content */
.filter-dropdown-content {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 2px;
  background-color: #2a3439;
  border-radius: 4px;
  max-height: 250px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  overflow: hidden;
  border: 1px solid #4a5459;
}

@media (max-width: 767px) {
  .filter-dropdown-content {
    max-height: 200px;
  }
}

.filter-dropdown-search {
  padding: 0.5rem;
  border-bottom: 1px solid #4a5459;
}

.filter-dropdown-search input {
  background-color: #3a4449;
  border-color: #4a5459;
  color: #f6f1e9;
  font-size: 0.875rem;
  padding: 0.375rem 0.5rem;
}

.filter-dropdown-body {
  overflow-y: auto;
  flex: 1;
}

.filter-dropdown-items {
  padding: 0.25rem 0;
}

.filter-dropdown-item {
  padding: 0.25rem 0.75rem;
  cursor: pointer;

  &:hover {
    background-color: #3a4449;
  }
}

.custom-control-label {
  color: #f6f1e9;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  width: 100%;
}
</style>
